// Cross-tab session synchronization.
//
// Tabs publish an "auth-changed" signal whenever login/logout happens, and
// every tab also stores the last-known logged-in user id in localStorage.
// Subscribers re-check on broadcast receipt and on focus/visibility changes;
// if the tab's in-memory user id no longer matches localStorage, the tab
// reloads. This prevents a stale tab from operating as the previous user
// after a logout/login happened in another tab.

const CHANNEL_NAME = 'confirm-session';
const STORAGE_EVENT_KEY = 'confirm.sessionBroadcast.event';
export const LAST_KNOWN_ME_ID_KEY = 'confirm.lastKnownMeId';

export type SessionEvent = {
  type: 'auth-changed';
  tabId: string;
};

type Handler = (event: SessionEvent) => void;

const tabId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

let channel: BroadcastChannel | null = null;
const handlers = new Set<Handler>();

const getChannel = (): BroadcastChannel | null => {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.addEventListener('message', (e: MessageEvent<SessionEvent>) => {
    dispatchEvent(e.data);
  });
  return channel;
};

const dispatchEvent = (event: SessionEvent | null | undefined) => {
  if (!event || event.tabId === tabId) return;
  for (const handler of handlers) handler(event);
};

const onStorage = (event: StorageEvent) => {
  if (event.key !== STORAGE_EVENT_KEY || !event.newValue) return;
  try {
    dispatchEvent(JSON.parse(event.newValue) as SessionEvent);
  } catch {
    // Ignore malformed payloads.
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', onStorage);
}

export const publishAuthChanged = (): void => {
  const event: SessionEvent = { type: 'auth-changed', tabId };
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage(event);
      return;
    } catch {
      // Fall through to localStorage fallback.
    }
  }
  try {
    // Write-then-remove so a repeated identical write still fires `storage`
    // in other tabs.
    localStorage.setItem(STORAGE_EVENT_KEY, JSON.stringify(event));
    localStorage.removeItem(STORAGE_EVENT_KEY);
  } catch {
    // localStorage may be unavailable (private mode, quota); nothing to do.
  }
};

export const subscribeToAuthChanged = (handler: Handler): (() => void) => {
  handlers.add(handler);
  getChannel();
  return () => {
    handlers.delete(handler);
  };
};

export const setLastKnownMeId = (meId: number | string | null): void => {
  try {
    if (meId == null) {
      localStorage.removeItem(LAST_KNOWN_ME_ID_KEY);
    } else {
      localStorage.setItem(LAST_KNOWN_ME_ID_KEY, String(meId));
    }
  } catch {
    // localStorage unavailable; cross-tab check will simply no-op.
  }
};

export const getLastKnownMeId = (): string | null => {
  try {
    return localStorage.getItem(LAST_KNOWN_ME_ID_KEY);
  } catch {
    return null;
  }
};
